import { buildRiskRecognitionInvestigatePrompt } from '@cobliteam/ai/src/lib/pageContext';
import { useAIAssistant } from '@cobliteam/ai/src/store/useAIAssistant';
import { Analytics } from '@cobliteam/analytics';
import type { RiskRecognitionHighlight } from '@cobliteam/api-lib/src/dashboard/riskRecognitions/Types';
import { Location } from '@cobliteam/common-types';
import { useCreateRiskRecognitionSilence } from '@cobliteam/hooks/src/useEntity/useRiskRecognitions/useCreateRiskRecognitionSilence';
import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import { useTranslation } from '@cobliteam/i18n';
import { Environment } from '@cobliteam/react-environments';
import Drawer, { DrawerBody, DrawerClose, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from '@cobliteam/ui/components/Drawer';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { parseISO } from '@cobliteam/utils/date';
import { useKnockClient } from '@knocklabs/react';
import { createSerializer, parseAsInteger, parseAsString } from 'nuqs';
import { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import useNotificationDrawerStore from '../../core/store/useNotificationDrawerStore';
import { buildRiskRecognitionEventProps } from './analytics';
import { toDriverDetails, toDriverTitleCase, toInvestigation } from './mockInvestigation';
import { getHighlightFromFeedItem, RISK_DRAWER_PARAM } from './parse';
import RiskRecognitionDriverDetails from './RiskRecognitionDriverDetails';
import RiskRecognitionInvestigation from './RiskRecognitionInvestigation';
import useRiskRecognitionSlice from './store/useRiskSlice';

const RISK_RECOGNITION_TYPE_KEY = 'risk-recognition';

// Busca a mensagem específica pelo id (`knock.messages.get`) e popula o highlight ativo no store pra o
// drawer renderizar o conteúdo. Por id direto — não depende do que está carregado no feed (um deep link
// fresco não teria o item). A Message traz `source.key` + `data`, que é o que `getHighlightFromFeedItem` precisa.
const RiskRecognitionDeepLinkLoader = ({ itemId }: { itemId: string }) => {
  const knock = useKnockClient();
  const openDrawer = useNotificationDrawerStore((s) => s.openDrawer);

  useEffect(() => {
    let cancelled = false;
    knock.messages
      .get(itemId)
      .then((message) => {
        if (cancelled) return;
        const highlight = getHighlightFromFeedItem(message as never);
        if (highlight) openDrawer(RISK_RECOGNITION_TYPE_KEY, highlight);
      })
      .catch(() => {
        // Sem a mensagem (id inválido / sem permissão) o drawer fica vazio — graceful, sem quebrar a página.
      });

    return () => {
      cancelled = true;
    };
  }, [knock, itemId, openDrawer]);

  return null;
};

// Deep link aberto pelo sino do header carrega o id do item do feed em `?risk-recognition-drawer=<id>`.
// Gate = condição que o KnockInAppProvider usa pra montar o KnockProvider (knockInApp + apiKey + user),
// garantindo que `useKnockClient` tenha um provider acima e não derrube a página.
const RiskRecognitionDeepLinkResolver = ({ itemId }: { itemId: string }) => {
  const { knockInApp } = useFeatureFlags();
  const { getSession } = useUser();
  const session = getSession();
  const userId = session?.user?.id;
  const { knockAPIKey } = Environment.getInstance().getEnvironment();

  if (!(knockInApp && knockAPIKey && userId)) return null;

  return <RiskRecognitionDeepLinkLoader itemId={itemId} />;
};

// Type-safe builder for the /tracking deep link. `date` is epoch ms, read back as a number on /tracking.
const serializeTrackingLink = createSerializer({
  vehicle_id: parseAsString,
  date: parseAsInteger,
});

/**
 * Drawer do risco (self-contained). Lê o store genérico de drawer: só se considera aberto quando o tipo
 * ativo é o risco (`activeType === 'risk-recognition'`). Mantém o param de deep link legado próprio
 * (`?risk-recognition-drawer=<id>`) pra links já compartilhados continuarem abrindo.
 */
const RiskRecognitionDrawer = () => {
  const { t } = useTranslation('notifications');
  const { knockInApp } = useFeatureFlags();
  const activeType = useNotificationDrawerStore((s) => s.activeType);
  const isDrawerOpenStore = useNotificationDrawerStore((s) => s.isDrawerOpen);
  const activePayload = useNotificationDrawerStore((s) => s.activePayload);
  const closeDrawer = useNotificationDrawerStore((s) => s.closeDrawer);
  const followRouteToTracking = useRiskRecognitionSlice((s) => s.followRouteToTracking);
  const history = useHistory();
  const location = useLocation();
  const { openAISidepanelWithHiddenInput, resetNewThreadId, setThreadId } = useAIAssistant();
  const { mutate: createSilence } = useCreateRiskRecognitionSilence();

  const isRiskActive = activeType === RISK_RECOGNITION_TYPE_KEY;
  const highlight = isRiskActive ? (activePayload as RiskRecognitionHighlight | null) : null;

  // Soma ao gatilho do store (card/toast usam `openDrawer`); o param vem do deep link / feed do sino e
  // carrega o id do item (`?risk-recognition-drawer=<id>`) pra resolver o highlight via feed compartilhado.
  const deepLinkItemId = new URLSearchParams(location.search).get(RISK_DRAWER_PARAM);
  const isDrawerParamOpen = !!deepLinkItemId;
  const isOpen = (isDrawerOpenStore && isRiskActive) || isDrawerParamOpen;
  // Resolve o highlight do id só quando precisa: há deep link, ainda sem highlight no store, e o provider
  // do Knock existe (knockInApp on). O `id === 'true'` é o sentinel legado (sem id) — nada a resolver.
  const shouldResolveDeepLink = knockInApp && !highlight && !!deepLinkItemId && deepLinkItemId !== 'true';

  // Fecha o drawer de verdade: baixa a flag do store E tira o param do deep link. Só `closeDrawer()` não
  // basta quando o drawer foi aberto por `?risk-recognition-drawer=<id>` — o param mantém `isOpen` true e
  // o drawer fica aberto atrás do sidepanel. `replace` (sem nova entrada) também evita reabrir no reload.
  const closeDrawerAndClearParam = () => {
    closeDrawer();
    if (isDrawerParamOpen) {
      const params = new URLSearchParams(location.search);
      params.delete(RISK_DRAWER_PARAM);
      history.replace({ search: params.toString() });
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (open) return;
    Analytics.track('AI - Risk Recognition - Drawer Closed', buildRiskRecognitionEventProps(highlight));
    closeDrawerAndClearParam();
  };

  const handleViewRoute = () => {
    Analytics.track('AI - Risk Recognition - View Route Clicked', buildRiskRecognitionEventProps(highlight));
    const vehicleId = highlight?.metadata?.vehicle_id;
    if (!vehicleId) return;
    const locatedAt = highlight?.metadata?.last_location?.located_at;
    // parseISO keeps timezone parsing deterministic across browsers (Safari chokes on raw Date.parse).
    const parsed = locatedAt ? parseISO(locatedAt).getTime() : Number.NaN;
    const date = Number.isNaN(parsed) ? Date.now() : parsed;
    // Pin this bucket so the floating card keeps following the acted-on alert onto /tracking.
    followRouteToTracking(highlight?.time_bucket_end ?? null);
    closeDrawer();
    history.push(serializeTrackingLink(Location.TRACKING, { vehicle_id: vehicleId, date }));
  };

  const handleInvestigate = async () => {
    if (!highlight) return;
    Analytics.track('AI - Risk Recognition - Investigate Clicked', buildRiskRecognitionEventProps(highlight));
    Analytics.track('AI - Sidepanel - Opened', {
      source: 'risk-recognition-drawer',
      deviceId: highlight.metadata?.device_id,
      vehicleId: highlight.metadata?.vehicle_id,
    });

    const mainFactorTitle = highlight.risk_factors.find((factor) => factor.type === 'main')?.title ?? highlight.risk_factors[0]?.title;
    const prompt = buildRiskRecognitionInvestigatePrompt({
      driverName: highlight.metadata?.driver_name ? toDriverTitleCase(highlight.metadata.driver_name) : undefined,
      mainFactorTitle,
      driverId: highlight.metadata?.driver_id,
      vehicleId: highlight.metadata?.vehicle_id,
      licensePlate: highlight.metadata?.vehicle_plate,
      deviceId: highlight.metadata?.device_id,
      ruleName: highlight.metadata?.rule_name,
    });

    closeDrawerAndClearParam();
    await setThreadId('');
    resetNewThreadId();
    openAISidepanelWithHiddenInput(prompt);
  };

  // The anchor's `href` handles the WhatsApp navigation; here we record the fleet-wide silence (~1h,
  // contacted) alongside analytics. Fired from both WhatsApp entry points (body link + footer button) —
  // server is idempotent on (detection_id, reason). Guarded: no detection_id → skip the POST, still navigate.
  const handleTalkToDriver = () => {
    Analytics.track('AI - Risk Recognition - Talk To Driver Clicked', buildRiskRecognitionEventProps(highlight));
    const detectionId = highlight?.metadata?.detection_id;
    if (detectionId) createSilence({ detectionId, reason: 'contacted' });
  };

  const driverDetails = highlight ? toDriverDetails(highlight) : null;
  const investigation = highlight ? toInvestigation(highlight) : null;
  const driverWhatsAppUrl = driverDetails?.whatsAppUrl ?? '';

  return (
    <Drawer open={isOpen} onOpenChange={handleOpenChange}>
      {shouldResolveDeepLink && <RiskRecognitionDeepLinkResolver itemId={deepLinkItemId} />}
      <DrawerContent size='md'>
        <DrawerHeader>
          <div className='flex items-center gap-2'>
            <DrawerTitle>{t('risk_recognition.drawer.title')}</DrawerTitle>
            <Icon name='info' size='md' className='text-[#60646c]' aria-hidden />
          </div>
          <DrawerClose />
        </DrawerHeader>

        <DrawerBody className='min-h-0 flex-1 flex-col gap-3 bg-white pt-6'>
          {driverDetails && <RiskRecognitionDriverDetails data={driverDetails} onViewRoute={handleViewRoute} onTalkToDriver={handleTalkToDriver} />}
          {investigation && <RiskRecognitionInvestigation data={investigation} />}
        </DrawerBody>

        <DrawerFooter className='items-center justify-between'>
          <DrawerClose asChild>
            <button
              type='button'
              className='inline-flex h-8 items-center rounded-md px-2 transition-colors hover:bg-[#dcebfe] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'
            >
              <Typography as='span' variant='text-md-semibold' className='text-[#1c53e6]'>
                {t('risk_recognition.drawer.close')}
              </Typography>
            </button>
          </DrawerClose>

          <div className='flex items-center gap-2'>
            {/* The span wrapper lets the tooltip fire over the disabled button. */}
            {driverWhatsAppUrl ? (
              <a
                href={driverWhatsAppUrl}
                target='_blank'
                rel='noopener noreferrer'
                onClick={handleTalkToDriver}
                className='inline-flex h-9 items-center rounded-lg bg-[#dcebfe] px-4 transition-colors hover:bg-[#cfe2fb] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'
              >
                <Typography as='span' variant='text-md-medium' className='whitespace-nowrap text-[#07074f]'>
                  {t('risk_recognition.drawer.talk_to_driver')}
                </Typography>
              </a>
            ) : (
              <Tooltip content={t('risk_recognition.drawer.no_driver_phone')} position='top'>
                <span className='inline-flex'>
                  <button type='button' disabled className='inline-flex h-9 cursor-not-allowed items-center rounded-lg bg-[#dcebfe] px-4 opacity-50'>
                    <Typography as='span' variant='text-md-medium' className='whitespace-nowrap text-[#07074f]'>
                      {t('risk_recognition.drawer.talk_to_driver')}
                    </Typography>
                  </button>
                </span>
              </Tooltip>
            )}

            <button
              type='button'
              onClick={handleInvestigate}
              className={cn(
                'cursor-pointer [background-image:linear-gradient(248.25deg,#8b5cf6_20%,#0248e9_60%,#001bb8_100%)]',
                'inline-flex h-9 items-center gap-2 rounded-lg px-4 transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'
              )}
            >
              <Icon name='search' size='sm' className='text-[#fafafa]' aria-hidden />
              <Typography as='span' variant='text-md-medium' className='whitespace-nowrap text-[#fafafa]'>
                {t('risk_recognition.drawer.investigate')}
              </Typography>
            </button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default RiskRecognitionDrawer;
